import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge } from "./shared";
import { IMG } from "../data/site";
import { Plane, Route, Briefcase, Users, Hotel, ShoppingBag, Home, Building2 } from "lucide-react";

const CHAIN = [
  { icon: Plane, label: "Airport" },
  { icon: Route, label: "Connectivity" },
  { icon: Briefcase, label: "Business" },
  { icon: Users, label: "Employment" },
  { icon: Hotel, label: "Hospitality" },
  { icon: ShoppingBag, label: "Retail" },
  { icon: Home, label: "Housing" },
  { icon: Building2, label: "Urban Development" },
];

const ECONOMY = [
  ["Aviation", "Noida International Airport (DXN) - Phase 1 operational at Jewar since 15 June 2026."],
  ["Business", "Office districts and corporate campuses proposed around the airport zone."],
  ["Logistics", "Cargo, warehousing and multi-modal freight infrastructure forming nearby."],
  ["Hospitality", "Aerocity-style hotels and serviced accommodation proposed along the corridor."],
  ["Retail", "High-street retail and commercial formats, including Aerocity Yamuna by Gaur."],
  ["Entertainment", "The proposed International Film City adds a cultural economy to the corridor."],
  ["Residential", "Gaur Yamuna City and surrounding sectors house the ecosystem's workforce."],
  ["Employment", "Each layer of the airport economy compounds long-term housing demand."],
];

export function AirportEffect() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <>
      <section id="airport" ref={ref} className="relative py-28 lg:py-44 overflow-hidden bg-[#0A0A0A]" data-testid="airport-section">
        <motion.div className="absolute inset-0" style={{ y }}>
          <img src={IMG.airport} alt="Noida International Airport aircraft" className="w-full h-[120%] object-cover img-cinematic opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-black/60 to-[#0A0A0A]" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTag>The Airport Effect</SectionTag>
          <div className="max-w-3xl">
            <SectionTitle className="text-5xl sm:text-6xl lg:text-7xl">
              The Airport <span className="text-gold-gradient italic">Has Arrived.</span>
            </SectionTitle>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="airport-status-line">
                <StatusBadge status="Operational" />
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#71717A]">Phase 1 &mdash; commercial flights since 15 June 2026 &middot; 12 MPPA capacity</span>
              </div>
              <p className="mt-6 text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-xl">
                The opportunity isn&rsquo;t simply being close to an airport. The larger opportunity is
                participating in the economic ecosystem that develops around an airport &mdash; the
                potential demand drivers that historically follow major aviation infrastructure.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 lg:mt-24 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="airport-chain">
            {CHAIN.map((c, i) => (
              <Reveal key={c.label} delay={0.1 + i * 0.09}>
                <div className="relative flex flex-col items-center text-center gap-3 py-6 px-2 border border-[#D4AF37]/15 bg-black/50 backdrop-blur-sm rounded-[2px] hover:border-[#D4AF37]/50 transition-colors duration-500 h-full">
                  <c.icon size={22} strokeWidth={1.4} className="text-[#D4AF37]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">{c.label}</span>
                  {i < CHAIN.length - 1 && (
                    <span className="hidden lg:block absolute top-1/2 -right-[13px] w-[10px] h-px bg-[#D4AF37]/50" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-28 lg:py-40 bg-[#0D0D0D]" data-testid="airport-city-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTag>From Airport to Airport City</SectionTag>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-end mb-14">
            <SectionTitle>
              An Airport Is Infrastructure.<br />
              <span className="text-gold-gradient italic">An Airport City Is an Economy.</span>
            </SectionTitle>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-2" data-testid="airport-city-status-legend">
                <StatusBadge status="Operational" />
                <StatusBadge status="Under Development" />
                <StatusBadge status="Announced" />
                <StatusBadge status="Proposed" />
              </div>
              <p className="mt-4 text-sm text-[#71717A] leading-relaxed max-w-md">
                Every element of the corridor carries an honest status. We never present proposed
                developments as completed.
              </p>
            </Reveal>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="airport-city-grid">
            {ECONOMY.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="card-lux p-6 rounded-[2px] h-full">
                  <p className="font-serif-lux text-lg text-[#F3E5AB] mb-2">{title}</p>
                  <p className="text-[13px] text-[#71717A] leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
